import React from 'react';
import Image from 'next/image';
import { Trash2, Ban } from 'lucide-react';
import Tooltip from '../Tooltip';

interface PostCardMenuPopupProps {
  isOpen: boolean;
  position: { top: number; left: number } | null;
  onClose: () => void;
  onMakePrivate: () => void;
  onReport: () => void;
  onBlock?: () => void;
  onEdit?: () => void;
  isPublic?: boolean;
  loading?: boolean;
  currentUserId?: string;
  postSenderId?: string;
  isCommunityPost?: boolean;
  isCommunityAdmin?: boolean;
  onDeleteCommunityPost?: () => void;
  onReportCommunityPost?: () => void;
  onEditCommunityPost?: () => void;
  /** Non-community joydrop ⋮: who may change visibility and whether the action is Make private vs Make public. */
  visibilityMenu?: { canChange: boolean; myWantsPublic: boolean } | null;
}

const PostCardMenuPopup: React.FC<PostCardMenuPopupProps> = ({
  isOpen,
  position,
  onClose,
  onMakePrivate,
  onReport,
  onBlock,
  onEdit,
  isPublic = true,
  loading = false,
  currentUserId,
  postSenderId,
  isCommunityPost = false,
  isCommunityAdmin = false,
  onDeleteCommunityPost,
  onReportCommunityPost,
  onEditCommunityPost,
  visibilityMenu,
}) => {
  if (!isOpen || !position) return null;

  const isCurrentUserSender = currentUserId && postSenderId && currentUserId === postSenderId;
  const vm =
    visibilityMenu ??
    ({
      canChange: !!isCurrentUserSender,
      myWantsPublic: isPublic,
    } as const);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />

      {/* Popup */}
      <div
        data-post-menu-popup
        className="fixed z-50 w-[204px] rounded-[8px] border-t-[1px] border-r-[3px] border-b-[3px] border-l-[1px] border-[#151515] bg-white shadow-lg flex flex-col gap-2 p-2"
        style={{
          top: position.top,
          left: position.left,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isCommunityPost ? (
          <>
            {onEditCommunityPost && isCurrentUserSender && (
              <button
                onClick={onEditCommunityPost}
                className="w-[188px] h-[48px] rounded-[8px] cursor-pointer flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                Edit
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
            {onReportCommunityPost && !isCurrentUserSender && (
              <button
                onClick={onReportCommunityPost}
                className="w-[188px] h-[48px] rounded-[8px] cursor-pointer flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                Report
                <Image src="/PostCard/flag.svg" alt="report" className="w-5 h-5 brightness-50" width={20} height={20} />
              </button>
            )}
            {onBlock && !isCurrentUserSender && (
              <button
                onClick={onBlock}
                className="w-[188px] h-[48px] rounded-[8px] cursor-pointer flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                Block user
                <Ban size={20} className="text-red-500" />
              </button>
            )}
            {onDeleteCommunityPost && isCommunityAdmin && (
              <button
                onClick={onDeleteCommunityPost}
                className="w-[188px] h-[48px] rounded-[8px] cursor-pointer flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-red-600 hover:bg-red-50 transition-colors duration-200 font-medium"
              >
                Delete
                <Trash2 size={20} className="text-red-500" />
              </button>
            )}
          </>
        ) : (
          <>
            {onEdit && isCurrentUserSender && (
              <button
                onClick={onEdit}
                className="w-[188px] h-[48px] rounded-[8px] cursor-pointer flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-gray-700 hover:bg-gray-100 opacity-100 transition-colors duration-200"
              >
                Edit
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
            <Tooltip
              id="make-private-tooltip"
              content="Making joydrops private removes it from your feed and cannot be undone."
              position="top"
              variant="dark"
              size="sm"
            >
              <button
                onClick={onMakePrivate}
                disabled={loading || !vm.canChange}
                className="w-[188px] h-[48px] rounded-[8px] cursor-pointer flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-gray-700 hover:bg-gray-100 opacity-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? 'Updating...'
                  : vm.myWantsPublic
                    ? 'Make private'
                    : 'Make public'}
                <Image src="/PostCard/crossed-eye.svg" alt="private" className="w-5 h-5" width={20} height={20} />
              </button>
            </Tooltip>
            {!isCurrentUserSender && (
              <button
                onClick={onReport}
                className="w-[188px] h-[48px] cursor-pointer rounded-[8px] flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-gray-700 hover:bg-gray-100 opacity-100 transition-colors duration-200"
              >
                Report
                <Image src="/PostCard/flag.svg" alt="report" className="w-5 h-5" width={20} height={20} />
              </button>
            )}
            {!isCurrentUserSender && onBlock && (
              <button
                onClick={onBlock}
                className="w-[188px] h-[48px] cursor-pointer rounded-[8px] flex items-center justify-between gap-2 pt-[14px] pb-[14px] pl-[12px] pr-[12px] text-left text-red-600 hover:bg-red-50 opacity-100 transition-colors duration-200"
              >
                Block user
                <Ban size={20} className="text-red-500" />
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default PostCardMenuPopup;
